import { useEffect, useState, useRef } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "../components/Layout";
import api, { formatApiError } from "../api";
import { ReportView } from "./admin/ResultsTab";
import RolePlayHub from "./RolePlay";
import SalesPitchAttempt from "./participant/SalesPitchAttempt";
import SalesCertHub from "./participant/SalesCertHub";
import SalesCertMCQ from "./participant/SalesCertMCQ";
import SalesCertRolePlay from "./participant/SalesCertRolePlay";
import SalesCertReport from "./participant/SalesCertReport";
import RequireProctor from "../components/RequireProctor";
import ProctorGate from "../components/ProctorGate";
import { useProctor } from "../hooks/useProctor";
import { useRouteTracker } from "../hooks/useActivityLogger";
import { ArrowRight, ChevronLeft, ChevronRight, CheckCircle2, Sparkles, MessageSquare, ShieldCheck, AlertTriangle } from "lucide-react";

export default function ParticipantApp() {
  useRouteTracker();
  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-[1440px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="assessment/:assignmentId" element={<AssessmentFlow />} />
          <Route path="roleplay/:assignmentId" element={<RolePlayHub />} />
          <Route path="roleplay/:assignmentId/:exerciseId" element={<RolePlayHub />} />
          <Route path="salespitch" element={<SalesPitchAttempt />} />
          <Route path="salescert" element={<SalesCertHub />} />
          <Route path="salescert/mcq" element={<RequireProctor label="MCQ"><SalesCertMCQ /></RequireProctor>} />
          <Route path="salescert/pitch" element={<RequireProctor label="Sales Pitch"><SalesPitchAttempt /></RequireProctor>} />
          <Route path="salescert/roleplay" element={<RequireProctor label="Role Play"><SalesCertRolePlay /></RequireProctor>} />
          <Route path="salescert/report" element={<SalesCertReport />} />
          <Route path="report/:assignmentId" element={<Report />} />
        </Routes>
      </main>
    </div>
  );
}

function Dashboard() {
  const [assignments, setAssignments] = useState(null);
  const [programs, setPrograms] = useState(null);
  useEffect(() => {
    api.get("/participant/assignments").then((r) => setAssignments(r.data));
    api.get("/participant/programs").then((r) => setPrograms(r.data)).catch(() => setPrograms([]));
  }, []);

  if (!assignments) return <div className="text-center text-[#878787] py-20 text-sm">Loading…</div>;

  const statusBadge = (s) => ({
    invited: { cls: "badge-neutral", label: "Invited" },
    in_progress: { cls: "badge-info", label: "In progress" },
    completed: { cls: "badge-success", label: "Completed" },
  })[s] || { cls: "badge-neutral", label: s };

  return (
    <div className="space-y-10 fade-in">
      <div>
        <div className="overline mb-2">PARTICIPANT WORKSPACE</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">Your programmes</h1>
        <p className="text-[#525252] text-sm mt-2 max-w-3xl leading-relaxed">
          Every programme you have been invited to appears here. Each has its own timing, structure, and report.
        </p>
      </div>

      {programs && programs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="programmes-grid">
          {programs.map((p) => {
            const b = statusBadge(p.status);
            return (
              <div key={p.program_key} className="card p-6 flex flex-col" data-testid={`programme-card-${p.program_key}`}>
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="w-10 h-10 flex items-center justify-center text-white font-bold" style={{ background: p.cover_hex }}>
                    {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <span className={`badge ${b.cls}`} data-testid={`programme-status-${p.program_key}`}>{b.label}</span>
                </div>
                <div className="overline mb-1">{p.duration_min} min · {p.sections.length} sections</div>
                <h3 className="font-heading font-bold text-xl mb-2">{p.name}</h3>
                <p className="text-[13px] text-[#525252] leading-relaxed mb-4 flex-1">{p.description}</p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {p.sections.map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252]">{s}</span>
                  ))}
                </div>
                {p.assignment_id && p.status !== "completed" ? (
                  <Link to={`/participant/assessment/${p.assignment_id}`} className="btn btn-primary justify-center" data-testid={`programme-open-${p.program_key}`}>
                    Open programme <ArrowRight size={14} />
                  </Link>
                ) : p.status === "completed" ? (
                  <span className="btn btn-secondary justify-center !cursor-default opacity-70" data-testid={`programme-completed-${p.program_key}`}>
                    <CheckCircle2 size={14} /> Completed
                  </span>
                ) : p.program_key === "vi_sales_certification_2026" ? (
                  <Link to="/participant/salescert" className="btn btn-primary justify-center" data-testid={`programme-open-${p.program_key}`}>
                    Open certification <ArrowRight size={14} />
                  </Link>
                ) : (
                  <span className="btn btn-secondary justify-center !cursor-default opacity-70" data-testid={`programme-invited-${p.program_key}`}>
                    Awaiting content · check back soon
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {assignments.length > 0 && (
        <div>
          <div className="overline mb-2">CURRENT PROGRAMME DETAILS</div>
          <h2 className="font-heading font-bold text-2xl tracking-tight">Your assessment journey — VI Competency Assessment</h2>
          <p className="text-[#525252] text-sm mt-2 max-w-3xl leading-relaxed">
            Two phases: <strong>Phase 1 — Digital Assessment</strong> (SJT, knowledge, self-rating, open response · ~45 min). <strong>Phase 2 — Live AI Role-Play</strong> (two 10-min conversations with AI characters · ~25 min). Do them in any order. Your full report is generated after both phases.
          </p>
        </div>
      )}

      {assignments.map((a) => (
        <div key={a.id} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`assignment-card-${a.id}`}>
          {/* Phase 1: Digital */}
          <div className="card p-6 flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div className="overline">PHASE 1 · DIGITAL</div>
              <span className={`badge ${
                a.status === "scored" ? "badge-success" :
                a.status === "submitted" ? "badge-info" : "badge-neutral"
              }`}>{a.status === "pending" ? "Not started" : a.status}</span>
            </div>
            <h3 className="font-heading font-bold text-xl mb-2">Digital Assessment</h3>
            <p className="text-[13px] text-[#525252] leading-relaxed mb-4 flex-1">
              4 modules — Situational Judgment, Knowledge Check, Self-Rating, Open Response. ~45 minutes. Save in one sitting.
            </p>
            <div className="text-xs text-[#878787] mb-4">
              Cycle: {a.cycle?.name} · {a.cycle?.start_date} → {a.cycle?.end_date}
            </div>
            {(a.status === "pending" || a.status === "in_progress") ? (
              <Link to={`/participant/assessment/${a.id}`} className="btn btn-primary justify-center" data-testid={`start-assessment-${a.id}`}>
                Start digital assessment <ArrowRight size={14} />
              </Link>
            ) : (
              <span className="btn btn-secondary justify-center !cursor-default opacity-70">
                <CheckCircle2 size={14} /> Submitted
              </span>
            )}
          </div>

          {/* Phase 2: AI Role-Play */}
          <div className="card p-6 flex flex-col border-2 !border-[#EE2737]/30 relative">
            <div className="absolute top-0 right-0 bg-[#EE2737] text-white text-[9px] uppercase tracking-[0.15em] font-bold px-3 py-1">PRIMARY</div>
            <div className="flex items-start justify-between mb-3">
              <div className="overline !text-[#EE2737]">PHASE 2 · AI ENGINE</div>
              <span className="badge badge-red"><Sparkles size={11} /> 35% weight</span>
            </div>
            <h3 className="font-heading font-bold text-xl mb-2">Live AI Role-Play</h3>
            <p className="text-[13px] text-[#525252] leading-relaxed mb-4 flex-1">
              Two live conversations — one with an AI customer CIO, one with an AI channel partner. Live BARS scoring on your reasoning, structure, and executive presence.
            </p>
            <div className="flex items-center gap-3 text-xs text-[#878787] mb-4">
              <span className="flex items-center gap-1"><MessageSquare size={12} />2 exercises · ~25 min</span>
            </div>
            <Link to={`/participant/roleplay/${a.id}`} className="btn btn-primary justify-center" data-testid={`start-roleplay-${a.id}`}>
              <Sparkles size={14} /> Enter AI Engine
            </Link>
          </div>

          {/* View report */}
          {(a.status === "submitted" || a.status === "scored") && (
            <div className="md:col-span-2">
              <Link to={`/participant/report/${a.id}`} className="btn btn-secondary" data-testid={`view-report-${a.id}`}>
                View my report <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      ))}
      {assignments.length === 0 && (!programs || programs.length === 0) && (
        <div className="card p-8 text-center text-[#878787]" data-testid="participant-empty-state">
          You haven&apos;t been invited to any programme yet. Check back after your invitation email arrives.
        </div>
      )}
    </div>
  );
}

const MODULES = [
  { key: "sjt", label: "Situational Judgment", desc: "Read each scenario and choose the response you believe is most effective." },
  { key: "mcq", label: "Knowledge Check", desc: "Quick recall on B2B sales fundamentals." },
  { key: "self", label: "Self-Rating", desc: "Rate yourself on each competency · 1 Foundational → 5 Expert." },
  { key: "open", label: "Open Response", desc: "Two written prompts. Take your time — quality matters." },
];

function AssessmentFlow() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [step, setStep] = useState(0);
  const [sjtAns, setSjtAns] = useState({});
  const [mcqAns, setMcqAns] = useState({});
  const [selfRatings, setSelfRatings] = useState({});
  const [openResp, setOpenResp] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [proctored, setProctored] = useState(false);

  const proctor = useProctor({ assignmentId, enabled: proctored });
  const burstWatcherRef = useRef(null);
  if (!burstWatcherRef.current && proctor.watchTypingBurst) {
    burstWatcherRef.current = proctor.watchTypingBurst();
  }

  useEffect(() => { api.get(`/assessment/questions?assignment_id=${assignmentId}`).then((r) => setQuestions(r.data)); }, [assignmentId]);

  // Auto-submit on 3 warnings
  useEffect(() => {
    if (proctor.autoSubmit && !submitting) {
      proctor.logEvent?.("auto_submitted", { reason: "3 warnings" });
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proctor.autoSubmit]);

  if (!questions) return <div className="text-center text-[#878787] py-20">Loading questions…</div>;

  if (!proctored) {
    return <ProctorGate assignmentId={assignmentId} onReady={() => setProctored(true)} />;
  }

  const totalSteps = MODULES.length;
  const progress = Math.round(((step + 1) / totalSteps) * 100);

  const submit = async () => {
    setSubmitting(true); setErr("");
    try {
      const r = await api.post("/participant/submit", {
        assignment_id: assignmentId,
        sjt_answers: sjtAns,
        mcq_answers: mcqAns,
        self_ratings: selfRatings,
        open_responses: openResp,
      });
      if (r.data.ok) navigate(`/participant/report/${assignmentId}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto fade-in">
      {/* Hidden video element drives webcam capture for useProctor */}
      <video ref={proctor.videoRef} muted playsInline autoPlay style={{ display: "none" }} />
      <canvas ref={proctor.canvasRef} style={{ display: "none" }} />

      {/* Proctor status pill */}
      <div className="fixed top-20 right-4 z-40 flex flex-col items-end gap-2 max-w-xs" data-testid="proctor-status">
        <div className={`flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider ${
          proctor.warnings >= 2 ? "bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]" : "bg-[#D1FAE5] text-[#065F46] border border-[#10B981]"
        }`}>
          <ShieldCheck size={12} />
          PROCTORED · Warnings {proctor.warnings}/3
        </div>
        {proctor.lastFlash && (
          <div className="bg-[#0A0A0A] text-white px-3 py-2 text-[12px] leading-relaxed shadow-lg max-w-xs animate-pulse" data-testid="proctor-flash">
            <AlertTriangle size={12} className="inline mr-1.5" /> {proctor.lastFlash}
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="sticky top-16 bg-[#F8F9FA] -mx-4 sm:-mx-8 px-4 sm:px-8 py-4 z-10 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-3 text-xs">
          <span className="overline">{MODULES[step].label} · Module {step + 1} of {totalSteps}</span>
          <span className="font-heading font-black text-sm text-[#EE2737]">{progress}%</span>
        </div>
        <div className="progress-bar"><div style={{ width: `${progress}%` }} /></div>
      </div>

      <div className="py-12">
        <div className="mb-10">
          <div className="overline mb-3">{MODULES[step].label}</div>
          <h2 className="font-heading font-black text-3xl tracking-tighter">{MODULES[step].label}</h2>
          <p className="text-[#525252] text-sm mt-2">{MODULES[step].desc}</p>
        </div>

        {step === 0 && (
          <div className="space-y-10">
            {questions.sjt.map((q, i) => (
              <SJTItem key={q.id} q={q} idx={i + 1} value={sjtAns[q.id]} onChange={(v) => setSjtAns({ ...sjtAns, [q.id]: v })} />
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-8">
            {questions.mcq.map((q, i) => (
              <MCQItem key={q.id} q={q} idx={i + 1} value={mcqAns[q.id]} onChange={(v) => setMcqAns({ ...mcqAns, [q.id]: v })} />
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8">
            {questions.competencies.map((c) => (
              <SelfRating key={c.key} comp={c} value={selfRatings[c.key]} onChange={(v) => setSelfRatings({ ...selfRatings, [c.key]: v })} labels={questions.proficiency_labels} />
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8">
            {questions.open.map((q) => (
              <OpenItem key={q.id} q={q} value={openResp[q.id] || ""} onChange={(v) => {
                burstWatcherRef.current?.(v);
                setOpenResp({ ...openResp, [q.id]: v });
              }} />
            ))}
          </div>
        )}

        {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 mt-6 rounded-sm">{err}</div>}

        <div className="flex justify-between mt-12 pt-6 border-t border-[#E5E7EB]">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="btn btn-ghost" data-testid="prev-step">
            <ChevronLeft size={16} /> Previous
          </button>
          {step < totalSteps - 1 ? (
            <button onClick={() => setStep(step + 1)} className="btn btn-primary" data-testid="next-step">
              Next module <ChevronRight size={16} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} className="btn btn-primary" data-testid="submit-assessment">
              {submitting ? "Scoring with AI…" : <>Submit assessment <CheckCircle2 size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SJTItem({ q, idx, value, onChange }) {
  return (
    <div className="card p-6">
      <div className="overline mb-3">SCENARIO {idx} · {q.competency.replace(/_/g, " ")}</div>
      <p className="text-[15px] mb-5 leading-relaxed">{q.scenario}</p>
      <div className="space-y-2">
        {q.options.map((o) => (
          <label key={o.key}
            data-testid={`sjt-${q.id}-${o.key}`}
            className={`flex gap-3 items-start p-4 cursor-pointer border ${value === o.key ? "border-[#EE2737] bg-[#EE2737]/5" : "border-[#E5E7EB] hover:border-[#0A0A0A]"} transition-all`}>
            <input type="radio" name={q.id} className="mt-1 accent-[#EE2737]" checked={value === o.key} onChange={() => onChange(o.key)} />
            <span className="text-[14px] text-[#0A0A0A]"><span className="font-heading font-black mr-2">{o.key}.</span>{o.text || o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function MCQItem({ q, idx, value, onChange }) {
  return (
    <div className="card p-6">
      <div className="overline mb-3">Q{idx} · {q.competency.replace(/_/g, " ")}</div>
      <p className="font-semibold mb-4 text-[15px]">{q.question}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {q.options.map((o) => (
          <label key={o.key}
            data-testid={`mcq-${q.id}-${o.key}`}
            className={`flex gap-2 items-start p-3 cursor-pointer border ${value === o.key ? "border-[#EE2737] bg-[#EE2737]/5" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}>
            <input type="radio" name={q.id} className="mt-1 accent-[#EE2737]" checked={value === o.key} onChange={() => onChange(o.key)} />
            <span className="text-[13px]"><span className="font-heading font-bold mr-2">{o.key}.</span>{o.text || o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function SelfRating({ comp, value, onChange, labels }) {
  return (
    <div className="card p-6">
      <div className="overline mb-1">{comp.type}</div>
      <h4 className="font-heading font-bold text-lg mb-1">{comp.name}</h4>
      <p className="text-xs text-[#878787] mb-4">Where are you today?</p>
      <div className="flex" data-testid={`self-rating-${comp.key}`}>
        {[1,2,3,4,5].map((lvl) => (
          <button key={lvl} type="button"
            data-testid={`self-rating-${comp.key}-${lvl}`}
            onClick={() => onChange(lvl)}
            className={`bars-segment ${value === lvl ? "active" : ""}`}
          >
            <div className="font-heading font-black text-base">{lvl}</div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5">{labels[lvl]}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function OpenItem({ q, value, onChange }) {
  return (
    <div className="card p-6">
      <div className="overline mb-3">OPEN RESPONSE · {q.competency.replace(/_/g, " ")}</div>
      <p className="text-[15px] mb-4 leading-relaxed">{q.prompt}</p>
      <textarea
        rows={6}
        className="input font-body"
        placeholder="Type your response here..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`open-${q.id}`}
      />
      <div className="text-xs text-[#878787] mt-2">{value.split(/\s+/).filter(Boolean).length} words · scored by AI assessor</div>
    </div>
  );
}

function Report() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  return <ReportView assignmentId={assignmentId} onBack={() => navigate("/participant")} />;
}
